/**
 * Ties that cross a line break (MUS-522).
 *
 * A repertoire song is split into one phrase per line of music. When a tie
 * crosses a line break, the line that ends keeps the tie's start on its last
 * note and the next line keeps the tie's stop on its first note. Each phrase is
 * loaded into OSMD on its own, and OSMD only joins a tie stop to a tie start it
 * has already read in the same document: the next line's opening stop has
 * nothing to join, so OSMD drops it and the note reads as an ordinary, untied
 * note. Grading would then expect the held note to be struck again.
 *
 * This module reads the stop back out of the phrase's MusicXML, so the grading
 * fold (EventStream.addEvents2) can join it to the held note the previous line
 * ended on.
 */
import type { OSMDNoteData } from './OSMDIterator';

/** A note whose tie began in an earlier phrase (it continues that held note). */
export type TieContinuation = {
  /** MIDI pitch as written (before any transposition). */
  midiPitch: number;
  /** Onset from the phrase's start, in whole notes (OSMD's RealValue units). */
  onset: number;
  /** Written length in whole notes. */
  duration: number;
};

const STEP_SEMITONES: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const ONSET_TOLERANCE = 1e-4;

function childElements(parent: Element, tagName: string): Element[] {
  return Array.from(parent.children).filter((child) => child.tagName === tagName);
}

function firstChild(parent: Element, tagName: string): Element | undefined {
  return childElements(parent, tagName)[0];
}

function numberIn(parent: Element, tagName: string): number | null {
  const element = firstChild(parent, tagName);
  if (!element) return null;
  const value = Number(element.textContent);
  return Number.isFinite(value) ? value : null;
}

function midiPitchOf(note: Element): number | null {
  const pitch = firstChild(note, 'pitch');
  if (!pitch) return null;
  const step = firstChild(pitch, 'step')?.textContent?.trim() ?? '';
  const octave = numberIn(pitch, 'octave');
  if (!(step in STEP_SEMITONES) || octave === null) return null;
  return (octave + 1) * 12 + STEP_SEMITONES[step] + (numberIn(pitch, 'alter') ?? 0);
}

/**
 * The tie types a note carries: its <tie> elements, or, when it has none, its
 * <notations><tied> elements (a "continue" tied is both a stop and a start).
 */
function tieTypesOf(note: Element): Set<string> {
  const ties = childElements(note, 'tie').map((tie) => tie.getAttribute('type') ?? '');
  const tied = childElements(note, 'notations')
    .flatMap((notations) => childElements(notations, 'tied'))
    .map((element) => element.getAttribute('type') ?? '');
  const types = ties.length > 0 ? ties : tied;
  return new Set(types.flatMap((type) => (type === 'continue' ? ['stop', 'start'] : [type])));
}

type TimedNote = {
  element: Element;
  /** Pitch, staff and voice: which notes a tie can join. */
  lineKey: string;
  midiPitch: number;
  onset: number;
  duration: number;
};

/** Every pitched, timed note of one part, in reading order, with onset and length. */
function timedNotesOfPart(part: Element): TimedNote[] {
  const notes: TimedNote[] = [];
  let divisions = 1;
  let measureStart = 0;
  childElements(part, 'measure').forEach((measure) => {
    let position = 0;
    let measureLength = 0;
    let lastOnset = 0;
    Array.from(measure.children).forEach((child) => {
      if (child.tagName === 'attributes') {
        divisions = numberIn(child, 'divisions') ?? divisions;
        return;
      }
      const duration = numberIn(child, 'duration') ?? 0;
      if (child.tagName === 'backup') {
        position -= duration;
        return;
      }
      if (child.tagName === 'forward') {
        position += duration;
        measureLength = Math.max(measureLength, position);
        return;
      }
      if (child.tagName !== 'note' || firstChild(child, 'grace')) return;
      const isChordTone = firstChild(child, 'chord') !== undefined;
      const onset = isChordTone ? lastOnset : position;
      if (!isChordTone) {
        lastOnset = position;
        position += duration;
        measureLength = Math.max(measureLength, position);
      }
      const midiPitch = midiPitchOf(child);
      if (midiPitch === null) return;
      const staff = firstChild(child, 'staff')?.textContent?.trim() ?? '1';
      notes.push({
        element: child,
        lineKey: `${staff}:${midiPitch}`,
        midiPitch,
        onset: measureStart + onset / divisions / 4,
        duration: duration / divisions / 4,
      });
    });
    measureStart += measureLength / divisions / 4;
  });
  return notes;
}

/**
 * The notes in a phrase's MusicXML that continue a tie begun in an earlier
 * phrase: a tie stop with no start before it in this phrase, and, when that note
 * ties on again, every note the tie runs through until it ends. Empty when the
 * document cannot be read.
 */
export function readTieContinuationsFromPreviousPhrase(musicXml: string): TieContinuation[] {
  if (typeof DOMParser === 'undefined' || !musicXml) return [];
  const document = new DOMParser().parseFromString(musicXml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0) return [];

  return Array.from(document.getElementsByTagName('part')).flatMap((part) => {
    // Per pitch line: is a tie open, and did it begin in an earlier phrase?
    const openTies = new Map<string, 'thisPhrase' | 'earlierPhrase'>();
    return timedNotesOfPart(part).flatMap((note) => {
      const types = tieTypesOf(note.element);
      const open = openTies.get(note.lineKey);
      const continuesEarlierTie =
        types.has('stop') && (open === undefined || open === 'earlierPhrase');
      if (types.has('stop')) openTies.delete(note.lineKey);
      if (types.has('start')) {
        openTies.set(note.lineKey, continuesEarlierTie ? 'earlierPhrase' : 'thisPhrase');
      }
      return continuesEarlierTie
        ? [{ midiPitch: note.midiPitch, onset: note.onset, duration: note.duration }]
        : [];
    });
  });
}

/**
 * Flag the rendered notes that continue a tie from the previous phrase. A
 * rendered note matches a continuation by onset (from the phrase start) and by
 * pitch, the written pitch moved by the phrase's transposition. Returns new note
 * objects; the input is not changed.
 */
export function markTieContinuationsFromPreviousPhrase(
  noteData: OSMDNoteData[],
  continuations: TieContinuation[],
  phraseStartTimestamp: number,
  transposeSemitones: number,
): OSMDNoteData[] {
  if (continuations.length === 0) return noteData;
  return noteData.map((note) => {
    const continuesTie =
      !note.isRest &&
      continuations.some(
        (continuation) =>
          continuation.midiPitch + transposeSemitones === note.midiPitch &&
          Math.abs(continuation.onset - (note.timestamp - phraseStartTimestamp)) < ONSET_TOLERANCE,
      );
    return continuesTie ? { ...note, continuesTieFromPreviousPhrase: true } : note;
  });
}
